import React, { useMemo, useState } from 'react';
import SearchBox from '../../Components/SearchBox';
import Modal from '../../Modal/Modal';
import { StorylineDilemma } from '../../Types/StorylineDilemma';
import { Universe } from '../../Types/Universe';
import UniverseCard from './UniverseCard';
import UniverseCreationCard from './UniverseCreationCard';
import UniverseModal from './UniverseModal';

interface UniverseSelectionViewProps {
  universes: Universe[];
  selectedUniverseId?: string;
  searchString: string;
  onSearch: (searchString: string) => void;
  onSelectionChange: (universeId: string) => void;
  onUniverseCreated?: (
    universe: Universe,
    dilemmas: StorylineDilemma[],
  ) => void;
  onUniverseEdited?: (universe: Universe) => void;
}

const UniverseSelectionView: React.FC<UniverseSelectionViewProps> = ({
  universes,
  selectedUniverseId,
  searchString,
  onSearch,
  onSelectionChange,
  onUniverseCreated,
  onUniverseEdited,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [editingUniverse, setEditingUniverse] = useState<Universe | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleClick(universe: Universe) {
    if (selectedUniverseId === universe.id) {
      onSelectionChange?.(null);
    } else {
      onSelectionChange?.(universe.id);
    }
  }

  const handleUniverseCreated = (
    universe: Universe,
    dilemmas: StorylineDilemma[],
  ) => {
    onUniverseCreated?.(universe, dilemmas);
    onSelectionChange?.(universe.id);
  };

  const handleUniverseEdited = (universe: Universe) => {
    onUniverseEdited?.(universe);
    setEditingUniverse(null);
    setIsSubmitting(false);
  };

  const filteredUniverses = useMemo(
    () =>
      universes
        .filter((universe) =>
          universe.title.toLowerCase().includes(searchString.toLowerCase()),
        )
        .sort((a, b) => {
          // First sort by ownership (user's universes first)
          // Then sort alphabetically by title
          return (
            10 * ((b.createdByUserId ? 1 : 0) - (a.createdByUserId ? 1 : 0)) +
            a.title.localeCompare(b.title)
          );
        }),
    [searchString, universes],
  );

  return (
    <>
      <div className="flex justify-between items-center py-3 sticky top-0 z-10 bg-white">
        <div className="flex-col justify-start items-start gap-2 inline-flex">
          <div
            className="text-xl font-semibold text-left 
            max-lg:text-base max-lg:font-bold
            max-vxl:text-base max-vxl:font-bold
          "
          >
            Pick your Book's Universe
          </div>
          <div
            className="text-sm leading-5 max-w-[834px] text-neutral-800 text-opacity-70 text-left 
            max-lg:hidden
            max-vxl:hidden
          "
          >
            Each world holds endless possibilities. Which one speaks to your
            imagination?
          </div>
        </div>
        {onSearch && (
          <SearchBox
            searchString={searchString}
            onSearch={onSearch}
            placeholder="Search for a universe..."
          />
        )}
      </div>
      <div
        className="
                grid grid-cols-4 gap-3 overflow-auto
                max-md:grid-cols-2
                md:max-xl:grid-cols-3 md:max-xl:gap-3 md:max-xl:mt-1
              "
      >
        <UniverseCreationCard onUniverseCreated={handleUniverseCreated} />

        {filteredUniverses.map((universe) => (
          <UniverseCard
            key={universe.id}
            title={universe.title}
            description={universe.description}
            image={universe.image}
            isSelected={selectedUniverseId === universe.id}
            isEnabled={true}
            isActive={true}
            universe={universe}
            onClick={() => handleClick(universe)}
            onEdit={
              universe.createdByUserId
                ? () => setEditingUniverse(universe)
                : undefined
            }
          />
        ))}
      </div>

      <Modal
        isOpen={!!editingUniverse}
        onClose={() => setEditingUniverse(null)}
        allowImplicitClose={!isSubmitting}
        maxWidthClass="max-w-lg"
      >
        {editingUniverse && (
          <UniverseModal
            universe={editingUniverse}
            onStart={() => setIsSubmitting(true)}
            onSuccess={handleUniverseEdited}
            onCancel={() => {
              setEditingUniverse(null);
              setIsSubmitting(false);
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default UniverseSelectionView;
